/**
 * Leitwarte: kleine Bausteine (Karte, Kopf, Chips, Lade- und Fehlerzeilen)
 * und Format-Helfer, die alle Reiter teilen. Farben als Konstanten, damit
 * Inline-Styles und SVGs dieselben Töne nutzen wie leitwarte.css.
 */

import type { CSSProperties, ReactNode } from 'react';
import { ZUSTAND_ZEICHEN, type Zustand } from '@/lib/leitwarte-api';

export const T = {
  seite: '#0d0d0d',
  karte: '#1a1a19',
  rand: '#2a2a28',
  rand2: '#3a3a37',
  innen: '#232322',
  text: '#ffffff',
  sek: '#c3c2b7',
  ged: '#8a8a84',
  blau: '#3987e5',
  blau2: '#5598e7',
  orange: '#d95926',
  aqua: '#199e70',
  gut: '#0ca30c',
  warn: '#fab219',
  ernst: '#ec835a',
  krit: '#d03b3b',
} as const;

// ── Datum und Zahl ───────────────────────────────────────────────────────────

const MONATE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
const MONATE_KURZ = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
const TAGE = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
const TAGE_KURZ = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const p2 = (n: number) => String(n).padStart(2, '0');

/** Versteht '2026-03', '2026-03-15' (als lokales Datum) und ISO-Zeitstempel. */
export function parseDatum(s: string | null | undefined): Date | null {
  if (!s) return null;
  if (/^\d{4}-\d{2}$/.test(s)) return new Date(Number(s.slice(0, 4)), Number(s.slice(5, 7)) - 1, 1);
  // 'MM-DD' (Wochenlabel aus dem LMS, Montag der Woche): laufendes Jahr, bei Jahreswechsel das Vorjahr.
  if (/^\d{2}-\d{2}$/.test(s)) {
    const heute = new Date();
    const m = Number(s.slice(0, 2)) - 1;
    const jahr = m > heute.getMonth() + 1 ? heute.getFullYear() - 1 : heute.getFullYear();
    return new Date(jahr, m, Number(s.slice(3, 5)));
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [j, m, t] = s.split('-').map(Number);
    return new Date(j, m - 1, t);
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

export const isoTag = (d: Date) => `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
export const plusTage = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return isoTag(d);
};
export const datumLang = (d: Date) => `${TAGE[d.getDay()]}, ${d.getDate()}. ${MONATE[d.getMonth()]} ${d.getFullYear()}`;

export function uhrzeit(s: string | null | undefined): string {
  const d = parseDatum(s);
  return d ? `${p2(d.getHours())}:${p2(d.getMinutes())}` : '';
}
export function kurzDatum(s: string | null | undefined): string {
  const d = parseDatum(s);
  return d ? `${p2(d.getDate())}.${p2(d.getMonth() + 1)}.` : s ?? '';
}
export function kurzDatumZeit(s: string | null | undefined): string {
  const d = parseDatum(s);
  return d ? `${TAGE_KURZ[d.getDay()]} ${kurzDatum(s)} ${uhrzeit(s)}` : '';
}
export function monatName(s: string | null | undefined): string {
  const d = parseDatum(s);
  return d ? MONATE[d.getMonth()] : s ?? '';
}
export function monatKurz(s: string | null | undefined): string {
  const d = parseDatum(s);
  return d ? MONATE_KURZ[d.getMonth()] : s ?? '';
}
export const wochentagKurz = (i: number) => TAGE_KURZ[((i % 7) + 7) % 7];

/** 'heute', 'morgen', Wochentag innerhalb einer Woche, sonst 'TT.MM.'. */
export function tagLabel(s: string | null | undefined): string {
  const d = parseDatum(s);
  if (!d) return '';
  const heute = new Date();
  heute.setHours(0, 0, 0, 0);
  const ziel = new Date(d);
  ziel.setHours(0, 0, 0, 0);
  const diff = Math.round((ziel.getTime() - heute.getTime()) / 86400000);
  if (diff === 0) return 'heute';
  if (diff === 1) return 'morgen';
  if (diff === -1) return 'gestern';
  if (diff > 1 && diff < 7) return TAGE[ziel.getDay()];
  return kurzDatum(s);
}

/** Gerundete Veränderung mit Vorzeichen, z. B. '+54 %'. Ohne Vergleichswert null. */
export function prozentDelta(jetzt: number, davor: number): string | null {
  if (!(davor > 0)) return null;
  const p = Math.round(((jetzt - davor) / davor) * 100);
  if (p === 0) return '±0 %';
  return `${p > 0 ? '+' : '-'}${Math.abs(p)} %`;
}

export const kapital = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
export const summe = (xs: number[]) => xs.reduce((a, b) => a + (Number.isFinite(b) ? b : 0), 0);
export const prozent = (teil: number, ganz: number) => (ganz > 0 ? Math.round((teil / ganz) * 100) : 0);
export const mehrzahl = (n: number, eins: string, viele: string) => (n === 1 ? eins : viele);

/** „gerade eben", „vor 40 s", „vor 3 Min", „vor 2 Std", „vor 3 Tagen": Abstand zu jetzt (Standard: Date.now()). */
export function relativZeit(s: string | null | undefined, jetztMs = Date.now()): string {
  const d = parseDatum(s);
  if (!d) return '';
  const sek = Math.max(0, Math.round((jetztMs - d.getTime()) / 1000));
  if (sek < 10) return 'gerade eben';
  if (sek < 60) return `vor ${sek} s`;
  const min = Math.round(sek / 60);
  if (min < 60) return `vor ${min} Min`;
  const std = Math.round(min / 60);
  if (std < 24) return `vor ${std} Std`;
  const tage = Math.round(std / 24);
  return `vor ${tage} ${mehrzahl(tage, 'Tag', 'Tagen')}`;
}

// ── Bausteine ────────────────────────────────────────────────────────────────

export function Karte({ children, eng, style, className }: { children: ReactNode; eng?: boolean; style?: CSSProperties; className?: string }) {
  return (
    <section className={`lw-karte${eng ? ' lw-karte-eng' : ''}${className ? ` ${className}` : ''}`} style={style}>
      {children}
    </section>
  );
}

export function Kopf({ titel, sub, rechts }: { titel: ReactNode; sub?: ReactNode; rechts?: ReactNode }) {
  return (
    <div className="lw-kopf">
      <h2 className="lw-h2">{titel}</h2>
      {sub ? <span className="lw-sub">{sub}</span> : null}
      {rechts ? <span style={{ marginLeft: 'auto' }}>{rechts}</span> : null}
    </div>
  );
}

export function Punkt({ farbe, groesse = 8 }: { farbe: string; groesse?: number }) {
  return <span className="lw-punkt" style={{ background: farbe, width: groesse, height: groesse }} />;
}

export function Swatch({ farbe, rand, breite = 14, hoehe = 10 }: { farbe: string; rand?: string; breite?: number; hoehe?: number }) {
  return <span style={{ width: breite, height: hoehe, borderRadius: 2, background: farbe, display: 'inline-block', flexShrink: 0, border: rand ? `1px solid ${rand}` : undefined }} />;
}

export interface LegendeEintrag { farbe: string; label: ReactNode; art?: 'quadrat' | 'linie'; rand?: string }

export function Legende({ eintraege, rechts, groesse = 12 }: { eintraege: LegendeEintrag[]; rechts?: ReactNode; groesse?: number }) {
  return (
    <div style={{ display: 'flex', gap: 14, fontSize: groesse, color: T.sek, flexWrap: 'wrap', alignItems: 'center' }}>
      {eintraege.map((e, i) => (
        <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {e.art === 'linie' ? (
            <span style={{ width: 14, height: 2, background: e.farbe, display: 'inline-block' }} />
          ) : (
            <span style={{ width: 10, height: 10, borderRadius: 3, background: e.farbe, display: 'inline-block', border: e.rand ? `1px solid ${e.rand}` : undefined }} />
          )}
          {e.label}
        </span>
      ))}
      {rechts ? <span style={{ marginLeft: 'auto', color: T.sek }}>{rechts}</span> : null}
    </div>
  );
}

/** Zeichen einer Signal-Zelle (✓ ~ ! ✕ ●) in seiner Farbe; „keine" hat kein Zeichen. */
export function Zeichen({ zustand }: { zustand: Zustand }) {
  const z = ZUSTAND_ZEICHEN[zustand] ?? ZUSTAND_ZEICHEN.keine;
  if (!z.zeichen) return null;
  return <span style={{ color: z.farbe, fontWeight: 800 }}>{z.zeichen}</span>;
}

export function Laden({ text = 'Lade …' }: { text?: string }) {
  return <p className="lw-leise">{text}</p>;
}

export function Leer({ text }: { text: string }) {
  return <p className="lw-leise" style={{ padding: '6px 0' }}>{text}</p>;
}

export function Fehlerzeile({ error, retry }: { error: unknown; retry?: () => void }) {
  const msg = error instanceof Error ? error.message : error ? String(error) : 'keine Daten';
  return (
    <p className="lw-leise">
      Konnte nicht laden: {msg}
      {retry ? (
        <>
          {' · '}
          <button type="button" className="lw-link" onClick={retry}>Erneut versuchen</button>
        </>
      ) : null}
    </p>
  );
}

/** Kleine Hinweis-Kachel „Label: Wert". Ohne Doppelpunkt nur ein Satz in Sekundärfarbe. */
export function HinweisPille({ text }: { text: string }) {
  const i = text.indexOf(': ');
  const label = i > 0 ? text.slice(0, i) : null;
  const wert = i > 0 ? text.slice(i + 2) : text;
  return (
    <div className="lw-innen" style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 12 }}>
      {label ? <span className="lw-ged">{label}</span> : null}
      <span style={{ color: label ? T.text : T.sek, fontWeight: label ? 700 : 500 }}>{wert}</span>
    </div>
  );
}
